import Button from '@/common/components/Button';
import { CompanyBranchesTransformer } from '@/common/services/companyBranchesTransformer';
import { Mapper } from '@/common/services/mapper';
import { CompanyBranchData } from '@/common/types/companyBranches.type';
import { useCompanyBranches } from '@/features/privateCompanies/hooks/useCompanyBranches';
import { getLayoutedElements } from '@/features/privateCompanies/services/getLayoutElements';
import { CompanyBranchTransformerToStructureResponse } from '@/features/privateCompanies/types/companyBranches.type';
import { Node, ReactFlow, useReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import './style.css';
import { SearchNode } from './components/SearchNode';
import { ZoomButtons } from './components/ZoomButtons';
import CustomNode from './node'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper';
import LoadingSpinner from '@/common/components/LoadingSpinner';
import useCompany from '@/common/hooks/queries/useCompany';

const nodeTypes = {
  custom: CustomNode,
};

const Structure = ({ companyKind }: { companyKind?: string }) => {
  const { id } = useParams();
  const parentRef = useRef<HTMLDivElement>(null);
  const { setCenter } = useReactFlow();

  const [searchTerm, setSearchTerm] = useState('')
  const [matchedNodeIds, setMatchedNodeIds] = useState<string[]>([])
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null)

  const {
    data: { nodes, edges },
    isPending: isLoadingBranches,
    isFetching: isFetchingBranches,
    error: companyBranchesError = {} as any,
    refetch: refetchCompanyBranches,
  } = useCompanyBranches({
    filters: { companyId: id || '', pageSize: 1000 },
    mapper: Mapper.mapWithTransformer<CompanyBranchData[], CompanyBranchTransformerToStructureResponse>({
      transformer: CompanyBranchesTransformer.structureTransformer({
        companyId: id || '',
      }),
    }),
  });

  const { data: company } = useCompany({ id: id || '' });

  const consumptionControlledBy = company?.current_contract?.consumption_controlled_by

  const { nodes: layoutedNodes, edges: layoutedEdges } = useMemo(() => {
    return getLayoutedElements(nodes, edges);
  }, [id, nodes, edges]);

  const goToMatrixBranch = () => {
    const matrixBranch = layoutedNodes?.find((node: Node) => node.data.parent_id === null);

    if (matrixBranch) {
      const x = matrixBranch.position.x;
      const y = matrixBranch.position.y;
      setCenter(x + 172, y + 160, { zoom: 1 });
    }
  }

  useEffect(() => {
    if (!isLoadingBranches && layoutedNodes?.length) {
      setTimeout(goToMatrixBranch);
    }
  }, [isLoadingBranches, layoutedNodes?.length]);

  if (isLoadingBranches) return <div>Carregando...</div>;

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={companyBranchesError?.status}
      refetchFunction={refetchCompanyBranches}
    >
      <div data-testid='structure_panel_container' className='flex flex-col relative bg-white-100 rounded-md items-center p-4 gap-4'>
        <LoadingSpinner isLoading={isFetchingBranches} className='absolute top-4 self-center' />

        <div className='relative flex justify-between w-full'>
          <p>Estrutura da empresa</p>

          <div className='flex items-center gap-x-2'>
            <SearchNode
              nodes={layoutedNodes}
              onSearchChange={setSearchTerm}
              onMatchesChange={setMatchedNodeIds}
              onActiveChange={setActiveNodeId}
            />

            <ZoomButtons />

            <Link to='create'>
              <Button name='create_new_branch' operationKlassToCheckPermission='Admin::Client::CompanyBranches::Create'>
                Adicionar filial
              </Button>
            </Link>
          </div>
        </div>
        <div className='h-[calc(100vh-280px)] w-full' ref={parentRef}>
          <ReactFlow
            nodes={layoutedNodes?.map((node: Node) => ({
              ...node,
              data: { ...node.data,
                parentRef,
                companyKind,
                consumptionControlledBy,
                searchTerm,
                isMatch: matchedNodeIds.includes(node.id),
                isActiveMatch: node.id === activeNodeId,
              },
            }))}
            edges={layoutedEdges}
            nodeTypes={nodeTypes}
            fitView={true}
            fitViewOptions={{
              minZoom: 1,
            }}
            minZoom={0.2}
          />
        </div>
      </div>
    </ErrorMessageWrapper>
  );
};

export default Structure;
